import { inject } from "@angular/core";
import { CanActivateChildFn, CanActivateFn } from "@angular/router";

import { LektoAuthService } from "../services/essential/lekto-auth.service";
import { ProductConfigService } from "../services/products/product-config.service";
import { AppId, resolveTargetAppUrl, toAppId } from "../utils/app-url.util";

/**
 * Devolve ao app em que o SERVIDOR abriu a sessão quem chegou ao outro. A decisão por perfil é do
 * backend (`SelectProfileHandler`: `targetApp` + `additionalApps`), gravada junto com o papel por
 * `LektoAuthService.createProfileCookie`; este guard só a lê (`ARCHITECTURE.md`, "Autorização na
 * UI" e "destinos por endpoint"). Nenhuma lista de perfil por app vive no cliente.
 *
 * Mora no app que RECEBE a navegação, e não no login, porque é lá que o contrato põe a decisão
 * ("Retorno pós-login": sem gate de perfil/app no login). O login segue honrando o `returnUrl` por
 * cima do `targetApp` — e é justamente esse retorno que traz o perfil errado: a sessão anterior
 * expira numa tela do Admin, o interceptor de 401 manda ao login com `returnUrl=/admin/…`, e quem
 * entra em seguida, professor, chegava ao Admin. O mesmo vale para o domínio pelado (a raiz da origin
 * é a Plataforma) e para outra aba que troca o papel — por isso também roda em `canActivateChild`.
 *
 * Não age sem sessão: roda em paralelo com o `AuthGuard` no mesmo array, e o salto de app
 * atropelaria o redirecionamento dele ao login. Também não age quando o servidor não informou os apps
 * (personificação, link de acesso, cookie de sessão perdido): não adivinha pelo perfil.
 *
 * Não é autorização: o servidor continua validando cada requisição. É roteamento.
 */
export function appProfileGuard(currentApp: AppId): CanActivateFn & CanActivateChildFn {
  return () => {
    const authService = inject(LektoAuthService);
    const appAccess = authService.isLoggedIn() ? authService.getAppAccess() : null;

    if (!appAccess) {
      return true;
    }

    const openApps = [appAccess.targetApp, ...appAccess.additionalApps].map(toAppId);

    if (openApps.includes(currentApp)) {
      return true;
    }

    globalThis.location.href = resolveTargetAppUrl(
      appAccess.targetApp,
      inject(ProductConfigService)
    );
    return false;
  };
}

export const adminAppProfileGuard = appProfileGuard("admin");
export const platformAppProfileGuard = appProfileGuard("platform");
